import * as React from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'

import { cn } from '@/lib/utils'
import { Input } from '../primitives/Input'
import { Label } from '../primitives/Label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../primitives/Select'

interface CurrencyOption {
  code: string
  displayName: string
}

interface FormCurrencyInputProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label?: string
  placeholder?: string
  currenciesData: CurrencyOption[]
  rules?: Record<string, unknown>
  disabled?: boolean
  inputSize?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  showLabel?: boolean
}

export function FormCurrencyInput<T extends FieldValues>({
  control,
  name,
  label,
  placeholder = '0.00',
  currenciesData,
  rules,
  disabled = false,
  inputSize = 'md',
  className,
  showLabel = true,
}: FormCurrencyInputProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const value = field.value || { amount: undefined, codeCurrency: '' }

        const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const inputValue = e.target.value
          let newAmount: number | undefined = undefined

          if (inputValue && inputValue.trim() !== '') {
            const parsed = parseFloat(inputValue)
            if (!isNaN(parsed)) {
              const decimalPart = inputValue.split('.')[1]
              if (decimalPart && decimalPart.length > 10) {
                const parts = inputValue.split('.')
                const limited = parseFloat(
                  `${parts[0]}.${parts[1].substring(0, 10)}`
                )
                newAmount = limited
              } else {
                newAmount = parsed
              }
            }
          }

          field.onChange({
            ...value,
            amount: newAmount,
          })
        }

        const handleCurrencyChange = (newCurrency: string) => {
          field.onChange({
            ...value,
            codeCurrency: newCurrency,
          })
        }

        // Check if there's an amount-specific error
        const hasAmountError =
          fieldState.error?.message?.toLowerCase().includes('monto') ?? false

        // Check if there's a currency-specific error
        const hasCurrencyError =
          fieldState.error?.message?.toLowerCase().includes('moneda') ?? false

        return (
          <div className={cn('flex flex-col gap-2', className)}>
            {showLabel && label && (
              <Label htmlFor={name} required={!!rules?.required}>
                {label}
              </Label>
            )}
            <div className="flex gap-2">
              {/* Amount Input - 50% width */}
              <div className="flex w-1/2 flex-col">
                <Input
                  disabled={disabled}
                  error={hasAmountError}
                  id={name}
                  inputSize={inputSize}
                  min="0"
                  onChange={handleAmountChange}
                  placeholder={placeholder}
                  step="0.0000000001"
                  type="number"
                  value={value.amount || ''}
                />
                {(() => {
                  if (!fieldState.error?.message) return null
                  // Extract only the amount-related error message
                  const messages = fieldState.error.message.split('. ')
                  const amountError = messages.find((msg) =>
                    msg.toLowerCase().includes('monto')
                  )
                  return (
                    amountError && (
                      <span className="mt-1 text-xs text-red-600">
                        {amountError}
                      </span>
                    )
                  )
                })()}
              </div>

              {/* Currency Select - 50% width */}
              <div className="flex w-1/2 flex-col">
                <Select
                  disabled={disabled}
                  onValueChange={handleCurrencyChange}
                  value={value.codeCurrency || ''}
                >
                  <SelectTrigger
                    error={hasCurrencyError}
                    selectSize={inputSize}
                  >
                    <SelectValue placeholder="Moneda" />
                  </SelectTrigger>
                  <SelectContent>
                    {currenciesData.map((currency) => (
                      <SelectItem key={currency.code} value={currency.code}>
                        {currency.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(() => {
                  if (!fieldState.error?.message) return null
                  // Extract only the currency-related error message
                  const messages = fieldState.error.message.split('. ')
                  const currencyError = messages.find((msg) =>
                    msg.toLowerCase().includes('moneda')
                  )
                  return (
                    currencyError && (
                      <span className="mt-1 text-xs text-red-600">
                        {currencyError}
                      </span>
                    )
                  )
                })()}
              </div>
            </div>
          </div>
        )
      }}
      rules={rules}
    />
  )
}
